import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useReconciliation, useAddSubscriptionSensor, useAddAllSubscriptionSensors, useRemoveSubscriptionSensor } from '../hooks/useSubscriptions';
import type { ReconciliationSensor } from '../types';

interface ReconciliationListsProps {
  subscriptionId: string | null;
}

interface SensorListProps {
  sensors: ReconciliationSensor[];
  testid: string;
  emptyLabel: string;
  actionLabel: string;
  actionTestid: string;
  onAction: (sensorId: string) => void;
  pendingId: string | null;
}

function SensorList({
  sensors, testid, emptyLabel, actionLabel, actionTestid, onAction, pendingId,
}: SensorListProps) {
  if (sensors.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground" data-testid={`${testid}-empty`}>
        {emptyLabel}
      </p>
    );
  }
  return (
    <ul className="divide-y rounded-md border" data-testid={testid}>
      {sensors.map((s) => (
        <li
          key={s.id}
          className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm"
          data-testid="reconciliation-sensor-row"
        >
          <span className="min-w-0 flex-1 truncate font-medium">{s.name || s.customId || s.streamName || s.id}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{s.site?.name ?? '—'}</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 shrink-0"
            data-testid={actionTestid}
            disabled={pendingId === s.id}
            onClick={() => onAction(s.id)}
          >
            {actionLabel}
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * The two D-04 reconciliation lists for a subscription's renewal (S2b-2), read-only.
 * Consumes GET /v4/subscriptions/:id/reconciliation (the subscription_sensors junction):
 *   - unlinkedActive: live account sensors on no subscription → add candidates;
 *   - archivedLinked: linked-but-archived sensors → remove candidates.
 * Each row carries an Add / Remove action that writes the subscription_sensors
 * junction (S3), then refreshes the lists and the renewal counts.
 */
export default function ReconciliationLists({ subscriptionId }: ReconciliationListsProps) {
  const { t } = useTranslation('subscriptions');
  const { data, isLoading, isError } = useReconciliation(subscriptionId);
  const addMutation = useAddSubscriptionSensor(subscriptionId);
  const addAllMutation = useAddAllSubscriptionSensors(subscriptionId);
  const removeMutation = useRemoveSubscriptionSensor(subscriptionId);
  const [confirmAddAll, setConfirmAddAll] = useState(false);

  if (!subscriptionId) return null;

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="reconciliation-loading">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-sm text-muted-foreground" role="alert">
        {t('reconciliation.loadError', 'Could not load the reconciliation lists.')}
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="reconciliation">
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold">{t('reconciliation.unlinkedTitle', 'Active sensors not linked')}</h4>
          <Badge variant="secondary" data-testid="reconciliation-unlinked-count">{data.unlinkedActive.length}</Badge>
          {data.unlinkedActive.length > 0 && (
            <Button
              type="button"
              size="sm"
              className="ml-auto h-7 shrink-0"
              data-testid="reconciliation-add-all"
              disabled={addAllMutation.isPending}
              onClick={() => setConfirmAddAll(true)}
            >
              {t('reconciliation.addAll', 'Add all ({{count}})', { count: data.unlinkedActive.length })}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {t('reconciliation.unlinkedHint', 'On the account but not attached to any subscription — candidates to add.')}
        </p>
        <SensorList
          sensors={data.unlinkedActive}
          testid="reconciliation-unlinked"
          emptyLabel={t('reconciliation.noneToAdd', 'Nothing to add.')}
          actionLabel={t('reconciliation.add', 'Add')}
          actionTestid="reconciliation-add"
          onAction={(sensorId) => addMutation.mutate(sensorId)}
          pendingId={addMutation.isPending ? (addMutation.variables as string) : null}
        />
      </div>

      <AlertDialog open={confirmAddAll} onOpenChange={setConfirmAddAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('reconciliation.addAllConfirmTitle', 'Link all unlinked sensors?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('reconciliation.addAllConfirmBody', 'This will link {{count}} sensor(s) to this subscription in one action. The action is recorded in the account activity trail and each sensor can be unlinked individually afterwards.', { count: data.unlinkedActive.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('reconciliation.addAllCancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="reconciliation-add-all-confirm"
              onClick={() => addAllMutation.mutate(data.unlinkedActive.map((s) => s.id))}
            >
              {t('reconciliation.addAllConfirm', 'Link {{count}} sensor(s)', { count: data.unlinkedActive.length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-semibold">{t('reconciliation.archivedTitle', 'Archived but still linked')}</h4>
          <Badge variant="secondary" data-testid="reconciliation-archived-count">{data.archivedLinked.length}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('reconciliation.archivedHint', 'Linked to this subscription but archived — candidates to remove.')}
        </p>
        <SensorList
          sensors={data.archivedLinked}
          testid="reconciliation-archived"
          emptyLabel={t('reconciliation.noneToRemove', 'Nothing to remove.')}
          actionLabel={t('reconciliation.remove', 'Remove')}
          actionTestid="reconciliation-remove"
          onAction={(sensorId) => removeMutation.mutate(sensorId)}
          pendingId={removeMutation.isPending ? (removeMutation.variables as string) : null}
        />
      </div>
    </div>
  );
}
